import { forwardRef, useState, useEffect } from 'react';
import cn from 'classnames';
import { createBemBlockBuilder } from '@ebsco-ui/utils';
import LoadingButton from '@ebsco-ui/loading-button';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  if (!(Symbol.iterator in Object(arr) || Object.prototype.toString.call(arr) === "[object Arguments]")) {
    return;
  }

  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance");
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _nonIterableRest();
}

var NativeDropdownOptions = function NativeDropdownOptions(_ref) {
  var items = _ref.items,
      placeholder = _ref.placeholder,
      className = _ref.className;
  return /*#__PURE__*/jsxs(Fragment, {
    children: [placeholder && /*#__PURE__*/jsx("option", {
      hidden: true,
      value: "",
      className: className,
      children: placeholder
    }), items.map(function (_ref2) {
      var id = _ref2.id,
          value = _ref2.value,
          label = _ref2.label,
          lang = _ref2.lang;
      return /*#__PURE__*/jsx("option", {
        value: value,
        "data-auto": "eb-native-dropdown-option",
        className: className,
        lang: lang,
        children: label
      }, id);
    })]
  });
};

var NativeDropdown = forwardRef(function (_ref, ref) {
  var _filteredItems$find;

  var items = _ref.items,
      id = _ref.id,
      _ref$label = _ref.label,
      labelValue = _ref$label.value,
      _ref$label$display = _ref$label.display,
      displayLabel = _ref$label$display === void 0 ? true : _ref$label$display,
      onChange = _ref.onChange,
      _ref$initialSelectedV = _ref.initialSelectedValue,
      initialSelectedValue = _ref$initialSelectedV === void 0 ? "" : _ref$initialSelectedV,
      placeholder = _ref.placeholder,
      consumerClassName = _ref.className,
      _ref$styleType = _ref.styleType,
      styleType = _ref$styleType === void 0 ? "primary" : _ref$styleType,
      onOpen = _ref.onOpen,
      _ref$disabled = _ref.disabled,
      disabled = _ref$disabled === void 0 ? false : _ref$disabled,
      _ref$isLoading = _ref.isLoading,
      isLoading = _ref$isLoading === void 0 ? false : _ref$isLoading,
      loadingPlaceholder = _ref.loadingPlaceholder,
      announceLoadingStart = _ref.announceLoadingStart,
      announceLoadingEnd = _ref.announceLoadingEnd;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      renderLoadingComponent = _useState2[0],
      setRenderLoadingComponent = _useState2[1];

  var _useState3 = useState(initialSelectedValue),
      _useState4 = _slicedToArray(_useState3, 2),
      selected = _useState4[0],
      setSelected = _useState4[1];

  var getBlocksWith = createBemBlockBuilder(["eb-native-dropdown", consumerClassName]);
  var filteredItems = items.filter(function (item) {
    return !item.isDivider;
  });
  var selectedLabel = (_filteredItems$find = filteredItems.find(function (_ref2) {
    var value = _ref2.value;
    return value === selected;
  })) === null || _filteredItems$find === void 0 ? void 0 : _filteredItems$find.label.toString();
  useEffect(function () {
    /* We don't want the loading component to be rendered
     * if the dropdown is not supposed to have a loading state.*/
    if (isLoading && !renderLoadingComponent) {
      setRenderLoadingComponent(true);
    }
  }, [isLoading, renderLoadingComponent]);

  var handleChange = function handleChange(_ref3) {
    var selectedValue = _ref3.target.value;
    var selectedOption = filteredItems.find(function (_ref4) {
      var value = _ref4.value;
      return value === selectedValue;
    });

    if (!selectedOption) {
      return;
    }

    onChange && onChange(selectedOption);
    setSelected(selectedValue);
  };

  var handleOnOpen = function handleOnOpen(event) {
    if (!onOpen) {
      return;
    }

    if (!event.key) {
      onOpen(event);
      return;
    }

    switch (event.key) {
      case "Spacebar": // ie11 support

      case " ":
      case "Enter":
      case "ArrowUp":
      case "ArrowDown":
        onOpen(event);
    }
  };

  return /*#__PURE__*/jsxs("div", {
    className: cn(getBlocksWith(), getBlocksWith("--".concat(styleType)), disabled && getBlocksWith("--disabled")),
    children: [/*#__PURE__*/jsx("label", {
      className: cn(getBlocksWith("__label"), getBlocksWith("__label--".concat(styleType)), !displayLabel && getBlocksWith("__label--hidden")),
      htmlFor: id,
      children: labelValue
    }), /*#__PURE__*/jsxs("div", {
      className: cn(getBlocksWith("__menu-wrapper"), getBlocksWith("__menu-wrapper--".concat(styleType))),
      "data-auto": "eb-menu-wrapper",
      children: [renderLoadingComponent && /*#__PURE__*/jsx("div", {
        className: cn(getBlocksWith("__loader"), isLoading && getBlocksWith("__loader--loading")),
        children: /*#__PURE__*/jsx(LoadingButton, {
          tabIndex: -1,
          className: getBlocksWith("__loader-button"),
          styleType: styleType,
          loadingPlaceholder: loadingPlaceholder || selectedLabel || placeholder,
          announceLoadingStart: announceLoadingStart,
          announceLoadingEnd: announceLoadingEnd,
          isLoading: isLoading,
          disabled: disabled,
          children: loadingPlaceholder || selectedLabel || placeholder
        })
      }), /*#__PURE__*/jsx("select", {
        ref: ref,
        className: cn(getBlocksWith("__menu"), getBlocksWith("__menu--".concat(styleType)), isLoading && getBlocksWith("__menu--loading")),
        id: id,
        value: selected,
        onChange: handleChange,
        onClick: handleOnOpen,
        onKeyDown: handleOnOpen,
        "data-auto": "eb-native-dropdown",
        disabled: isLoading || disabled,
        children: /*#__PURE__*/jsx(NativeDropdownOptions, {
          items: filteredItems,
          placeholder: placeholder,
          className: cn(getBlocksWith("__option"), getBlocksWith("__option--".concat(styleType)))
        })
      })]
    })]
  });
});
NativeDropdown.displayName = "NativeDropdown";

export { NativeDropdown as default };
//# sourceMappingURL=native-dropdown-esm.js.map
